import { useEffect, useRef } from "react";
import { formatDuration, practice } from "../../lib/practice/session";
import styles from "./sessions.module.css";

// Shared pieces of the practice session views.
export const accuracyLevel = (accuracy) => accuracy == null ? undefined : accuracy >= 90 ? "great" : accuracy >= 70 ? "ok" : "low";
export const tempoRange = (tempos = []) => !tempos.length ? "" : Math.min(...tempos) === Math.max(...tempos) ? `${tempos[0]} BPM` : `${Math.min(...tempos)}–${Math.max(...tempos)} BPM`;
// The best scored take: highest accuracy, then highest tempo.
export const bestTake = (takes = []) => takes.filter((take) => take.accuracy != null).reduce((top, take) => !top || take.accuracy > top.accuracy || (take.accuracy === top.accuracy && take.tempo > top.tempo) ? take : top, null);
// Overall accuracy: takes weighted by how many notes they had.
export function overallAccuracy(items) {
  const takes = items.flatMap((item) => (item.takes || []).filter((take) => take.accuracy != null && take.notes));
  const notes = takes.reduce((sum, take) => sum + take.notes, 0);
  return notes ? Math.round(takes.reduce((sum, take) => sum + take.accuracy * take.notes, 0) / notes) : null;
}

export function Tiles({ seconds, playSeconds, rhythms, accuracy }) {
  return <div className={styles.tiles}>
    <div><strong>{formatDuration(seconds)}</strong><span>practiced</span></div>
    {playSeconds != null && <div><strong>{formatDuration(playSeconds)}</strong><span>playing</span></div>}
    <div><strong>{rhythms}</strong><span>{rhythms === 1 ? "rhythm" : "rhythms"}</span></div>
    {accuracy != null && <div><strong>{accuracy}%</strong><span>accuracy</span></div>}
  </div>;
}

// The rhythms in a session. `best` is a take ({ accuracy, tempo }) or derived from `takes`.
export function ItemList({ items, actions }) {
  return <ul className={styles.items}>{items.map((item, index) => {
    const best = item.best || bestTake(item.takes), takeCount = item.takeCount ?? item.takes?.length ?? 0;
    return <li key={item.key || index}>
      <strong>{item.name}</strong>
      <small>{[formatDuration(item.seconds), tempoRange(item.tempos), takeCount ? `${takeCount} ${takeCount === 1 ? "take" : "takes"}` : ""].filter(Boolean).join(" · ")}</small>
      {best ? <span className={styles.badge} data-level={accuracyLevel(best.accuracy)} title={`Best take: ${best.accuracy}% at ${best.tempo} BPM`}>{best.accuracy}% <small>@ {best.tempo}</small></span> : null}
      {actions?.(item, index)}
    </li>;
  })}</ul>;
}

// The microphone's level as a small meter, updated without re-rendering.
export function Meter() {
  const bar = useRef(null);
  useEffect(() => {
    let level = 0, frame = 0;
    const off = practice.onLevel((peak) => { level = Math.max(level * 0.9, peak); });
    const draw = () => { if (bar.current) bar.current.style.width = `${Math.min(100, Math.round(level * 250))}%`; level *= 0.92; frame = requestAnimationFrame(draw); };
    frame = requestAnimationFrame(draw);
    return () => { off(); cancelAnimationFrame(frame); };
  }, []);
  return <span className={styles.meter} aria-hidden="true"><span ref={bar} /></span>;
}
